/**
 * EvidenceViewerModule — admin Evidence Viewer (ODE-P07-T05).
 *
 * Renders the bounded evidence history (REQ-047) with the REQ-063
 * retention-until-ack flag, the durable acknowledgement state (DND §6) and a
 * read-only detail panel carrying the REQ-076 linkage plus the REQ-078
 * short-lived presigned media reference — all taken from the server
 * projection verbatim (the client never computes a checksum, an ack state or
 * a media URL).
 *
 * REQ-090 states rendered: pending (filters not submitted), loading, ready,
 * empty (no evidence for the filter), not found (unknown reference),
 * unauthorized (401), blocked (403 — no data shown), error (400/503/network),
 * plus the evidence-specific pending row (PENDING_UPLOAD ack state) and the
 * expired-media state (server-issued reference elapsed → re-fetch the detail).
 *
 * Accessibility: every row/field carries a stable `data-testid`, status is
 * never encoded by colour alone (text tokens are shown), and regions announce
 * updates politely.
 *
 * Traces to: ODE-PRD-REQ-047, ODE-PRD-REQ-063, ODE-PRD-REQ-076,
 *            ODE-PRD-REQ-078, ODE-PRD-REQ-090.
 */

import { useAuth } from '../../auth/AuthContext.js';
import type { AdminEvidenceDetail, AdminEvidenceRow } from '../../api/client.js';
import { StateView } from '../../components/StateView.js';
import type { ModuleState } from '../state.js';
import {
  isMediaExpired,
  useEvidenceViewer,
  type EvidenceDetailState,
  type EvidenceListState,
  type UseEvidenceViewerOptions,
} from './useEvidenceViewer.js';

/** Plain-Indonesian labels for the durable acknowledgement states. */
export const ACK_STATE_LABELS: Readonly<Record<string, string>> = {
  PENDING_UPLOAD: 'Menunggu unggah',
  SERVER_ACKNOWLEDGED: 'Diakui server',
};

/** Non-colour status tokens (never rely on colour alone). */
export const ACK_STATE_TOKENS: Readonly<Record<string, string>> = {
  PENDING_UPLOAD: '[MENUNGGU]',
  SERVER_ACKNOWLEDGED: '[DIAKUI]',
};

export interface EvidenceViewerSeams {
  readonly list?: UseEvidenceViewerOptions['list'];
  readonly detail?: UseEvidenceViewerOptions['detail'];
  /** Test seam: current wall clock for the expired-media state. */
  readonly now?: () => number;
}

function formatBytes(value: number | null): string {
  return value === null ? '—' : `${value} B`;
}

function AcknowledgementBadge({ row }: { readonly row: AdminEvidenceRow }): JSX.Element {
  const label = ACK_STATE_LABELS[row.ackState] ?? row.ackState;
  return (
    <span
      className={`badge badge-ack-${row.ackState.toLowerCase()}`}
      data-testid={`evidence-ack-${row.evidenceId}`}
    >
      {ACK_STATE_TOKENS[row.ackState] ?? '[—]'} {label}
    </span>
  );
}

export function EvidenceViewerModule({
  seams = {},
}: { readonly seams?: EvidenceViewerSeams } = {}): JSX.Element {
  const { session } = useAuth();
  const viewer = useEvidenceViewer({
    token: session?.token ?? null,
    ...(seams.list === undefined ? {} : { list: seams.list }),
    ...(seams.detail === undefined ? {} : { detail: seams.detail }),
  });
  const nowMs = (seams.now ?? (() => Date.now()))();
  const { form, setForm, state, detailState } = viewer;

  return (
    <section aria-labelledby="evidence-viewer-title" className="module">
      <h2 id="evidence-viewer-title">Riwayat Bukti Checkpoint</h2>
      <p className="module-description">
        Telusuri bukti (foto) checkpoint per Delivery Order (REQ-047): status
        pengakuan server, tanda retensi-sampai-diakui (REQ-063) dan tautan
        pratinjau media berumur pendek (REQ-078). Baca-saja — tidak ada
        perubahan data.
      </p>

      <form
        className="evidence-form"
        data-testid="evidence-form"
        onSubmit={(event) => {
          event.preventDefault();
          viewer.submit();
        }}
      >
        <label>
          ID Delivery Order
          <input
            data-testid="evidence-filter-do"
            value={form.deliveryOrderId}
            onChange={(event) => setForm({ deliveryOrderId: event.target.value })}
            placeholder="mis. 55555555-5555-4555-8555-555555555555"
          />
        </label>
        <label>
          Checkpoint
          <input
            data-testid="evidence-filter-checkpoint"
            value={form.checkpointId}
            onChange={(event) => setForm({ checkpointId: event.target.value })}
            placeholder="mis. CP4"
          />
        </label>
        <label>
          Jenis bukti
          <input
            data-testid="evidence-filter-type"
            value={form.evidenceType}
            onChange={(event) => setForm({ evidenceType: event.target.value })}
            placeholder="mis. loading_photo"
          />
        </label>
        <label>
          Status pengakuan
          <select
            data-testid="evidence-filter-ack"
            value={form.ackState}
            onChange={(event) =>
              setForm({ ackState: event.target.value as typeof form.ackState })
            }
          >
            <option value="">Semua status</option>
            <option value="PENDING_UPLOAD">PENDING_UPLOAD</option>
            <option value="SERVER_ACKNOWLEDGED">SERVER_ACKNOWLEDGED</option>
          </select>
        </label>
        <label>
          Batas baris
          <input
            data-testid="evidence-filter-limit"
            value={form.limit}
            onChange={(event) => setForm({ limit: event.target.value })}
            placeholder="25"
          />
        </label>
        <div className="evidence-actions">
          <button type="submit" data-testid="evidence-submit">
            Cari
          </button>
          <button type="button" data-testid="evidence-reset" onClick={viewer.reset}>
            Atur ulang
          </button>
        </div>
      </form>

      {state.kind === 'pending' ? (
        <StateView state={{ kind: 'pending' }} />
      ) : state.kind === 'ready' ? (
        <EvidenceResults
          state={state}
          onOpenDetail={viewer.openDetail}
          onLoadMore={viewer.loadMore}
        />
      ) : (
        <StateView state={toModuleState(state)} onRetry={viewer.submit} />
      )}

      <EvidenceDetailPanel
        state={detailState}
        nowMs={nowMs}
        onClose={viewer.closeDetail}
        onRefresh={viewer.refreshDetail}
      />
    </section>
  );
}

/** Maps the list state to the shared REQ-090 renderer. */
function toModuleState(state: EvidenceListState): ModuleState {
  switch (state.kind) {
    case 'pending':
      return { kind: 'pending' };
    case 'loading':
      return { kind: 'loading' };
    case 'ready':
      return { kind: 'empty', payload: state.page };
    case 'unauthorized':
      return { kind: 'unauthorized' };
    case 'blocked':
      return { kind: 'blocked', message: state.message };
    case 'error':
      return { kind: 'error', message: state.message };
  }
}

function EvidenceResults({
  state,
  onOpenDetail,
  onLoadMore,
}: {
  readonly state: Extract<EvidenceListState, { kind: 'ready' }>;
  readonly onOpenDetail: (evidenceId: string) => void;
  readonly onLoadMore: () => void;
}): JSX.Element {
  const { page } = state;
  if (page.rows.length === 0) {
    return (
      <div data-testid="evidence-empty" className="state state-empty">
        <h3>Belum ada data</h3>
        <p>Tidak ada bukti yang cocok dengan filter ini.</p>
      </div>
    );
  }
  return (
    <div data-testid="evidence-results">
      <p className="evidence-summary" data-testid="evidence-summary">
        {page.summary.total} bukti · {page.summary.acknowledged} diakui server ·{' '}
        {page.summary.pending} menunggu unggah
      </p>
      <table className="overview-table">
        <thead>
          <tr>
            <th scope="col">Delivery Order</th>
            <th scope="col">Checkpoint</th>
            <th scope="col">Jenis</th>
            <th scope="col">Status (DND §6)</th>
            <th scope="col">Retensi (REQ-063)</th>
            <th scope="col">Ukuran</th>
            <th scope="col">Diambil</th>
            <th scope="col">Aksi</th>
          </tr>
        </thead>
        <tbody>
          {page.rows.map((row) => (
            <EvidenceRowView key={row.evidenceId} row={row} onOpenDetail={onOpenDetail} />
          ))}
        </tbody>
      </table>
      {page.hasMore && (
        <button
          type="button"
          data-testid="evidence-load-more"
          onClick={onLoadMore}
          disabled={state.loadingMore}
        >
          {state.loadingMore ? 'Memuat…' : 'Muat lagi'}
        </button>
      )}
    </div>
  );
}

function EvidenceRowView({
  row,
  onOpenDetail,
}: {
  readonly row: AdminEvidenceRow;
  readonly onOpenDetail: (evidenceId: string) => void;
}): JSX.Element {
  return (
    <tr data-testid={`evidence-row-${row.evidenceId}`}>
      <td>{row.deliveryOrderId}</td>
      <td>{row.checkpointId}</td>
      <td data-testid={`evidence-type-${row.evidenceId}`}>{row.evidenceType}</td>
      <td>
        <AcknowledgementBadge row={row} />
      </td>
      <td data-testid={`evidence-retention-${row.evidenceId}`}>
        {row.retentionUntilAck ? 'Ditahan sampai diakui' : 'Sudah diakui server'}
      </td>
      <td>{formatBytes(row.sizeBytes)}</td>
      <td>{row.capturedAt ?? '—'}</td>
      <td>
        <button
          type="button"
          data-testid={`evidence-detail-${row.evidenceId}`}
          onClick={() => onOpenDetail(row.evidenceId)}
        >
          Detail
        </button>
      </td>
    </tr>
  );
}

function EvidenceDetailPanel({
  state,
  nowMs,
  onClose,
  onRefresh,
}: {
  readonly state: EvidenceDetailState;
  readonly nowMs: number;
  readonly onClose: () => void;
  readonly onRefresh: () => void;
}): JSX.Element | null {
  if (state.kind === 'idle') return null;

  return (
    <section aria-label="Detail bukti" data-testid="evidence-detail-panel" className="evidence-detail">
      <div className="evidence-detail-head">
        <h3>Detail Bukti</h3>
        <button type="button" data-testid="evidence-detail-close" onClick={onClose}>
          Tutup
        </button>
      </div>
      {state.kind === 'loading' && <StateView state={{ kind: 'loading' }} />}
      {state.kind === 'not_found' && (
        <div role="alert" data-testid="evidence-detail-not-found" className="state state-error">
          <h3>Tidak ditemukan</h3>
          <p>Referensi bukti tidak dikenal.</p>
        </div>
      )}
      {state.kind === 'unauthorized' && <StateView state={{ kind: 'unauthorized' }} />}
      {state.kind === 'blocked' && <StateView state={{ kind: 'blocked', message: state.message }} />}
      {state.kind === 'error' && <StateView state={{ kind: 'error', message: state.message }} />}
      {state.kind === 'ready' && (
        <EvidenceDetailBody detail={state.detail} nowMs={nowMs} onRefresh={onRefresh} />
      )}
    </section>
  );
}

function EvidenceDetailBody({
  detail,
  nowMs,
  onRefresh,
}: {
  readonly detail: AdminEvidenceDetail;
  readonly nowMs: number;
  readonly onRefresh: () => void;
}): JSX.Element {
  const expired = isMediaExpired(detail, nowMs);
  return (
    <div data-testid="evidence-detail-ready">
      <dl className="evidence-detail-grid">
        <dt>Delivery Order</dt>
        <dd data-testid="detail-evidence-do">{detail.evidence.deliveryOrderId}</dd>
        <dt>Checkpoint</dt>
        <dd>{detail.evidence.checkpointId}</dd>
        <dt>Jenis</dt>
        <dd>{detail.evidence.evidenceType}</dd>
        <dt>ID bukti (server)</dt>
        <dd data-testid="detail-server-evidence-id">
          {detail.evidence.serverEvidenceId ?? '—'}
        </dd>
        <dt>SHA-256</dt>
        <dd data-testid="detail-evidence-checksum">{detail.evidence.checksum ?? '—'}</dd>
        <dt>Ukuran</dt>
        <dd>{formatBytes(detail.evidence.sizeBytes)}</dd>
        <dt>Tipe konten</dt>
        <dd>{detail.evidence.contentType ?? '—'}</dd>
        <dt>Diambil</dt>
        <dd>{detail.evidence.capturedAt ?? '—'}</dd>
        <dt>Status pengakuan</dt>
        <dd data-testid="detail-evidence-ack">{detail.evidence.ackState}</dd>
        <dt>Retensi (REQ-063)</dt>
        <dd data-testid="detail-evidence-retention">
          {detail.evidence.retentionUntilAck
            ? 'Ditahan di perangkat sampai diakui server'
            : 'Sudah diakui server'}
        </dd>
      </dl>

      <section aria-label="Tautan audit" data-testid="detail-evidence-linkage">
        <h4>Keterkaitan audit (REQ-076)</h4>
        <p>
          Penanda pengakuan: {detail.linkage.ackMarkerPresent ? 'ada' : 'tidak ada'}
        </p>
        <p data-testid="detail-evidence-acknowledged-by">
          Diverifikasi oleh: {detail.linkage.acknowledgedBy ?? '—'}
        </p>
        <p data-testid="detail-evidence-marker-sha256">
          SHA-256 penanda: {detail.linkage.ackMarkerKeySha256}
        </p>
      </section>

      <section aria-label="Media bukti" data-testid="detail-evidence-media">
        <h4>Media (REQ-078)</h4>
        {detail.mediaState === 'MISSING' && (
          <p role="alert" data-testid="evidence-media-missing" className="state state-error">
            Objek media tidak ditemukan di penyimpanan — metadata tetap ditampilkan,
            tanpa tautan media (tidak ada keberhasilan palsu).
          </p>
        )}
        {detail.media !== null && !expired && (
          <p data-testid="evidence-media-available">
            <a
              data-testid="evidence-media-link"
              href={detail.media.url}
              rel="noreferrer noopener"
              target="_blank"
            >
              Buka pratinjau media
            </a>{' '}
            · berlaku {detail.media.expiresSeconds} detik (sampai {detail.media.expiresAt})
          </p>
        )}
        {detail.media !== null && expired && (
          <p role="alert" data-testid="evidence-media-expired" className="state state-error">
            Tautan media kedaluwarsa — muat ulang detail untuk membuat tautan baru.
            <button type="button" data-testid="evidence-media-refresh" onClick={onRefresh}>
              Muat ulang
            </button>
          </p>
        )}
      </section>
    </div>
  );
}